/**
 * Ekip yönetimi.
 *
 * Kullanıcı SİLİNMEZ, arşivlenir: kimin hangi klinik kaydı yazdığı
 * sorusunun cevabı kaybolmamalı. Askıya alma girişi kapatır, arşivleme
 * koltuğu boşaltır; iki durumda da geçmiş kayıtlar yerinde kalır.
 */

import { useCallback, useEffect, useState } from 'react'
import { ApiHatasi, ROL_ADI, api, type EkipDurumu, type Oturum } from '../api'

function tarih(s: string | null): string {
  if (!s) return 'hiç girmedi'
  const d = new Date(s)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })
}

/** Geçici parola: uygulayıcı kendi uydurmasın diye üretiliyor */
function geciciParolaUret(): string {
  const harf = 'ABCDEFGHJKLMNPRSTUVYZabcdefghijkmnprstuvyz23456789'
  const sayi = new Uint32Array(16)
  crypto.getRandomValues(sayi)
  return Array.from(sayi, (n) => harf[n % harf.length]).join('')
}

export function Ekip({ rol }: { rol: string }) {
  const [durum, setDurum] = useState<EkipDurumu | null>(null)
  const [hata, setHata] = useState<string | null>(null)
  const [formAcik, setFormAcik] = useState(false)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      setDurum(await api.kullanicilar())
    } catch (e) {
      setHata(
        e instanceof ApiHatasi && e.durum === 403
          ? 'Ekip listesini görme yetkiniz yok.'
          : 'Ekip listesi alınamadı.',
      )
    }
  }, [])

  useEffect(() => {
    void yukle()
  }, [yukle])

  const sahip = rol === 'owner'

  if (hata) return <div className="uyari">{hata}</div>
  if (!durum) return <p className="alt">Yükleniyor…</p>

  return (
    <>
      <h1>Ekip</h1>
      <p className="alt">
        {durum.merkez} · {durum.koltuk.dolu}/{durum.koltuk.limit} koltuk kullanımda
      </p>

      <div className="dugmeler" style={{ marginBottom: 14 }}>
        <button className="birincil" onClick={() => setFormAcik((v) => !v)}>
          {formAcik ? 'Vazgeç' : 'Kullanıcı ekle'}
        </button>
      </div>

      {formAcik && (
        <YeniKullaniciFormu
          onIptal={() => setFormAcik(false)}
          onKayit={() => {
            setFormAcik(false)
            void yukle()
          }}
        />
      )}

      <table>
        <thead>
          <tr>
            <th>Ad soyad</th>
            <th>E-posta</th>
            <th>Rol</th>
            <th>Son giriş</th>
            <th>Durum</th>
            {sahip && <th />}
          </tr>
        </thead>
        <tbody>
          {durum.kullanicilar.map((k) => (
            <tr key={k.id}>
              <td>
                {k.unvan ? k.unvan + ' ' : ''}
                {k.adSoyad}
              </td>
              <td style={{ color: 'var(--muted)' }}>{k.eposta}</td>
              <td>{ROL_ADI[k.rol] ?? k.rol}</td>
              <td style={{ color: 'var(--muted)', fontSize: 13 }}>{tarih(k.sonGiris)}</td>
              <td>
                <span className={'rozet' + (k.durum === 'active' ? ' ok' : ' warn')}>
                  {k.durum === 'active' ? 'Aktif' : k.durum === 'suspended' ? 'Askıda' : k.durum}
                </span>
              </td>
              {sahip && (
                <td style={{ textAlign: 'right' }}>
                  {k.rol !== 'owner' && (
                    <KullaniciIslem
                      id={k.id}
                      adSoyad={k.adSoyad}
                      durum={k.durum}
                      onBitti={() => void yukle()}
                    />
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <Oturumlarim />
    </>
  )
}

/* ------------------------------------------------------------------ */

function KullaniciIslem({
  id,
  adSoyad,
  durum,
  onBitti,
}: {
  id: string
  adSoyad: string
  durum: string
  onBitti: () => void
}) {
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)
  const [arsivOnayi, setArsivOnayi] = useState(false)

  const calistir = async (f: () => Promise<unknown>) => {
    setHata(null)
    setBekliyor(true)
    try {
      await f()
      onBitti()
    } catch (e) {
      setHata(
        e instanceof ApiHatasi && e.kod === 'kendini_askiya_alamaz'
          ? 'Kendinizi askıya alamazsınız.'
          : 'İşlem yapılamadı.',
      )
    } finally {
      setBekliyor(false)
      setArsivOnayi(false)
    }
  }

  return (
    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      {hata && <span style={{ color: 'var(--bad)', fontSize: 12 }}>{hata}</span>}
      <button
        disabled={bekliyor}
        onClick={() =>
          void calistir(() =>
            api.kullaniciDurum(id, durum === 'active' ? 'suspended' : 'active'),
          )
        }
      >
        {durum === 'active' ? 'Askıya al' : 'Geri aç'}
      </button>
      {arsivOnayi ? (
        <button
          className="birincil"
          disabled={bekliyor}
          onClick={() => void calistir(() => api.kullaniciArsivle(id))}
        >
          {adSoyad} arşivlensin
        </button>
      ) : (
        <button disabled={bekliyor} onClick={() => setArsivOnayi(true)}>
          Arşivle
        </button>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

function YeniKullaniciFormu({
  onIptal,
  onKayit,
}: {
  onIptal: () => void
  onKayit: () => void
}) {
  const [adSoyad, setAdSoyad] = useState('')
  const [unvan, setUnvan] = useState('')
  const [eposta, setEposta] = useState('')
  const [rolSecim, setRol] = useState<'practitioner' | 'assistant' | 'trainee'>('practitioner')
  const [geciciParola] = useState(geciciParolaUret)
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)
  const [kopyalandi, setKopyalandi] = useState(false)
  const [eklendi, setEklendi] = useState(false)

  const gonder = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      await api.kullaniciEkle({
        eposta: eposta.trim(),
        adSoyad: adSoyad.trim(),
        unvan: unvan.trim() || undefined,
        rol: rolSecim,
        geciciParola,
      })
      setEklendi(true)
    } catch (err) {
      if (err instanceof ApiHatasi && err.kod === 'koltuk_dolu') {
        setHata('Abonelikteki koltuklar dolu. Koltuk eklemek için bizimle iletişime geçin.')
      } else if (err instanceof ApiHatasi && err.kod === 'eposta_kullanimda') {
        setHata('Bu e-posta merkezinizde zaten kayıtlı.')
      } else {
        setHata('Kullanıcı eklenemedi.')
      }
    } finally {
      setBekliyor(false)
    }
  }

  if (eklendi) {
    return (
      <div className="kart">
        <h2 style={{ marginTop: 0 }}>Kullanıcı eklendi</h2>
        <p style={{ marginTop: 0 }}>
          <strong>{adSoyad}</strong> için geçici parola aşağıda. Bu parola bir daha
          gösterilmeyecek; kullanıcıya kendiniz iletin ve ilk girişte değiştirmesini söyleyin.
        </p>
        <div className="baglanti">
          <code>{geciciParola}</code>
          <button
            onClick={() => {
              void navigator.clipboard
                .writeText(geciciParola)
                .then(() => setKopyalandi(true))
                .catch(() => setKopyalandi(false))
            }}
          >
            {kopyalandi ? 'Kopyalandı ✓' : 'Kopyala'}
          </button>
        </div>
        <div className="dugmeler">
          <button className="birincil" onClick={onKayit}>
            Tamam
          </button>
        </div>
      </div>
    )
  }

  return (
    <form className="kart" onSubmit={gonder}>
      <h2 style={{ marginTop: 0 }}>Yeni kullanıcı</h2>
      {hata && <div className="uyari">{hata}</div>}
      <div className="satir">
        <label>
          Ad soyad
          <input
            value={adSoyad}
            onChange={(e) => setAdSoyad(e.target.value)}
            required
            minLength={2}
            maxLength={120}
          />
        </label>
        <label>
          Unvan
          <input
            value={unvan}
            onChange={(e) => setUnvan(e.target.value)}
            maxLength={60}
            placeholder="Uzm. Psk."
          />
        </label>
        <label>
          E-posta
          <input
            type="email"
            value={eposta}
            onChange={(e) => setEposta(e.target.value)}
            required
            maxLength={200}
          />
        </label>
        <label>
          Rol
          <select value={rolSecim} onChange={(e) => setRol(e.target.value as never)}>
            <option value="practitioner">Psikolog — dosya açar, ölçüm başlatır</option>
            <option value="trainee">Stajyer — görür, ölçüm başlatamaz</option>
            <option value="assistant">Sekreter — randevu ve dosya listesi</option>
          </select>
        </label>
      </div>
      <p className="alt" style={{ marginBottom: 10 }}>
        Geçici parola otomatik üretiliyor ve kaydettikten sonra bir kez gösterilecek.
      </p>
      <div className="dugmeler">
        <button className="birincil" type="submit" disabled={bekliyor}>
          {bekliyor ? 'Ekleniyor…' : 'Ekle'}
        </button>
        <button type="button" onClick={onIptal}>
          Vazgeç
        </button>
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ */

function Oturumlarim() {
  const [liste, setListe] = useState<Oturum[] | null>(null)
  const [suanki, setSuanki] = useState('')
  const [bekliyor, setBekliyor] = useState(false)

  const yukle = useCallback(async () => {
    try {
      const r = await api.oturumlar()
      setListe(r.oturumlar)
      setSuanki(r.suanki)
    } catch {
      setListe([])
    }
  }, [])

  useEffect(() => {
    void yukle()
  }, [yukle])

  if (!liste || liste.length === 0) return null

  return (
    <>
      <h2>Açık oturumlarım</h2>
      <p className="alt">
        Hesabınıza şu anda bu cihazlardan girilmiş. Tanımadığınız bir satır varsa diğerlerini
        kapatıp parolanızı değiştirin.
      </p>
      <table>
        <thead>
          <tr>
            <th>Cihaz</th>
            <th>IP</th>
            <th>Açılış</th>
            <th>Son hareket</th>
          </tr>
        </thead>
        <tbody>
          {liste.map((o) => (
            <tr key={o.id}>
              <td>
                {o.tarayici ?? o.cihaz ?? 'bilinmiyor'}
                {o.id === suanki && (
                  <span className="rozet ok" style={{ marginLeft: 8 }}>
                    bu cihaz
                  </span>
                )}
              </td>
              <td style={{ color: 'var(--muted)' }}>{o.ip ?? '—'}</td>
              <td style={{ color: 'var(--muted)', fontSize: 13 }}>{tarih(o.acilis)}</td>
              <td style={{ color: 'var(--muted)', fontSize: 13 }}>{tarih(o.sonGorulme)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="dugmeler">
        <button
          disabled={bekliyor || liste.length < 2}
          onClick={() => {
            setBekliyor(true)
            void api
              .digerleriniKapat()
              .then(() => yukle())
              .finally(() => setBekliyor(false))
          }}
        >
          Diğer oturumları kapat
        </button>
      </div>
    </>
  )
}
